import { ANNOTATION_PROPS } from "./annotationConstants";
import { ignoringDynamicContent } from "./annotationsUtils";

const DEFAULT_MATH_ML_CLASS_NAME = ANNOTATION_PROPS.ANNOTATION_MATH_ML_CLASS_NAME;

type AnnotationBookmarkOffsets = {
  start: number;
  end: number;
  backward: boolean;
  commonAncestorCssSelector: string;
};

type RangyRangeFactory = {
  createRange: () => RangyCharacterRange;
};

type RangyCharacterRange = Range & {
  selectNodeContents: (node: Node) => void;
  setStart: (node: Node, offset: number) => void;
  setEnd: (node: Node, offset: number) => void;
  text: () => string;
  collapsed: boolean;
  intersection?: (range: Range) => Range | null;
};

type HighlightApplier = {
  applyToRange: (range: Range) => void;
};

type RangySelection = {
  text: () => string;
  moveToBookmark: (bookmark: unknown) => void;
  setSingleRange: (range: unknown, direction?: string) => void;
};

type LegacyBookmark = {
  backward: boolean;
  rangeBookmarks: Array<{
    start: number;
    end: number;
    containerNode: Node;
  }>;
};

const NULL_CHAR_END_OFFSET_PROBE_LIMIT = 64;

const normalizeSelectionText = (text: string): string => {
  return ignoringDynamicContent(text).replace(/\0/g, "").replace(/\s+/g, " ").trim();
};

const isTextNodeInsideSvg = (textNode: Text): boolean => {
  let parent: Node | null = textNode.parentNode;
  while (parent) {
    if (
      parent.nodeType === 1 &&
      (parent as Element).namespaceURI === "http://www.w3.org/2000/svg"
    ) {
      return true;
    }
    parent = parent.parentNode;
  }
  return false;
};

const isChildOfMath = (node: Node): boolean => {
  let current: Node | null = node;
  while (current) {
    if (current.nodeType === 1 && (current as Element).tagName.toLowerCase() === "math") {
      return true;
    }
    current = current.parentNode;
  }
  return false;
};

const isUserSelectNoneTextNode = (textNode: Text): boolean => {
  const parent = textNode.parentElement;
  if (!parent || typeof window === "undefined") {
    return false;
  }
  try {
    const style = window.getComputedStyle(parent);
    return (
      style.userSelect === "none" ||
      (style as CSSStyleDeclaration & { webkitUserSelect?: string }).webkitUserSelect === "none"
    );
  } catch {
    return false;
  }
};

const isExcludedFromCharacterIndex = (textNode: Text, mathMlClassName: string): boolean => {
  if (isTextNodeInsideSvg(textNode)) {
    return true;
  }
  if (textNode.parentElement?.closest(`.${mathMlClassName}`)) {
    return true;
  }
  if (isUserSelectNoneTextNode(textNode) && !isChildOfMath(textNode)) {
    return true;
  }
  const parent = textNode.parentElement;
  if (parent && /^(script|style)$/i.test(parent.nodeName)) {
    return true;
  }
  return false;
};

const countIndexedCharactersBefore = (
  container: Node,
  boundaryNode: Node,
  boundaryOffset: number,
  mathMlClassName: string,
): number => {
  const boundary = document.createRange();
  boundary.setStart(boundaryNode, boundaryOffset);
  boundary.collapse(true);

  let count = 0;
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
  let textNode: Node | null;

  while ((textNode = walker.nextNode())) {
    const text = textNode as Text;
    if (isExcludedFromCharacterIndex(text, mathMlClassName)) {
      continue;
    }

    const nodeStart = document.createRange();
    nodeStart.selectNodeContents(text);
    nodeStart.collapse(true);
    const nodeEnd = document.createRange();
    nodeEnd.selectNodeContents(text);
    nodeEnd.collapse(false);

    if (boundary.compareBoundaryPoints(Range.START_TO_START, nodeStart) <= 0) {
      break;
    }

    if (boundary.compareBoundaryPoints(Range.START_TO_START, nodeEnd) >= 0) {
      count += text.length;
      continue;
    }

    const partial = document.createRange();
    partial.setStart(text, 0);
    partial.setEnd(boundaryNode, boundaryOffset);
    count += partial.toString().length;
    break;
  }

  return count;
};

const rangeToCharacterOffsets = (
  range: Range,
  container: Node,
  mathMlClassName: string = DEFAULT_MATH_ML_CLASS_NAME,
): { start: number; end: number } => {
  const start = countIndexedCharactersBefore(
    container,
    range.startContainer,
    range.startOffset,
    mathMlClassName,
  );
  const end = countIndexedCharactersBefore(
    container,
    range.endContainer,
    range.endOffset,
    mathMlClassName,
  );
  return { start, end: Math.max(end, start) };
};

const createRangeFromCharacterOffsets = (
  rangyApi: RangyRangeFactory,
  container: Node,
  start: number,
  end: number,
  mathMlClassName: string = DEFAULT_MATH_ML_CLASS_NAME,
): RangyCharacterRange => {
  const range = rangyApi.createRange();
  let charIndex = 0;
  let startSet = false;
  const normalizedEnd = Math.max(end, start);

  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
  let textNode: Node | null;

  while ((textNode = walker.nextNode())) {
    const text = textNode as Text;
    if (isExcludedFromCharacterIndex(text, mathMlClassName)) {
      continue;
    }

    const nodeStart = charIndex;
    const nodeEnd = charIndex + text.length;

    if (!startSet && start >= nodeStart && start <= nodeEnd) {
      range.setStart(text, start - nodeStart);
      startSet = true;
    }

    if (startSet && normalizedEnd >= nodeStart && normalizedEnd <= nodeEnd) {
      range.setEnd(text, normalizedEnd - nodeStart);
      return range;
    }

    charIndex = nodeEnd;
  }

  if (!startSet) {
    range.selectNodeContents(container);
    range.collapse(true);
  } else if (range.collapsed) {
    range.setEnd(range.startContainer, range.startOffset);
  }

  return range;
};

const getTextFromCharacterOffsets = (
  rangyApi: RangyRangeFactory,
  container: Node,
  start: number,
  end: number,
  mathMlClassName: string = DEFAULT_MATH_ML_CLASS_NAME,
): string => {
  const range = createRangeFromCharacterOffsets(rangyApi, container, start, end, mathMlClassName);
  return range.text?.() ?? range.toString();
};

const rangeIntersectsTextNode = (range: Range, textNode: Text): boolean => {
  if (!range.intersectsNode(textNode)) {
    return false;
  }

  const nodeRange = document.createRange();
  nodeRange.selectNodeContents(textNode);
  return (
    range.compareBoundaryPoints(Range.END_TO_START, nodeRange) < 0 &&
    range.compareBoundaryPoints(Range.START_TO_END, nodeRange) > 0
  );
};

const getHtmlTextNodesInSelectionRange = (range: Range, mathMlClassName: string): Text[] => {
  const result: Text[] = [];
  const root =
    range.commonAncestorContainer.nodeType === Node.TEXT_NODE
      ? range.commonAncestorContainer.parentElement
      : (range.commonAncestorContainer as Element);

  if (!root) {
    return result;
  }

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let node: Node | null;
  while ((node = walker.nextNode())) {
    const textNode = node as Text;
    if (isTextNodeInsideSvg(textNode)) {
      continue;
    }
    if (textNode.parentElement?.closest(`.${mathMlClassName}`)) {
      continue;
    }
    if (isChildOfMath(textNode)) {
      continue;
    }
    if (!rangeIntersectsTextNode(range, textNode)) {
      continue;
    }
    result.push(textNode);
  }

  return result;
};

const applyHighlightToHtmlTextInRange = (
  rangyApi: RangyRangeFactory,
  range: Range,
  applier: HighlightApplier,
  mathMlClassName: string,
): void => {
  getHtmlTextNodesInSelectionRange(range, mathMlClassName).forEach((textNode) => {
    const nodeRange = rangyApi.createRange();
    nodeRange.selectNodeContents(textNode);

    const intersection = nodeRange.intersection?.(range);
    if (intersection && !intersection.collapsed) {
      applier.applyToRange(intersection);
      return;
    }

    let startOffset = 0;
    let endOffset = textNode.length;
    if (range.startContainer === textNode) {
      startOffset = range.startOffset;
    }
    if (range.endContainer === textNode) {
      endOffset = range.endOffset;
    }

    if (startOffset >= endOffset) {
      return;
    }

    nodeRange.setStart(textNode, startOffset);
    nodeRange.setEnd(textNode, endOffset);
    if (!nodeRange.collapsed) {
      applier.applyToRange(nodeRange);
    }
  });
};

const resolveCharacterRangeEndForText = (
  rangyApi: RangyRangeFactory,
  container: Node,
  start: number,
  expectedSelectedText: string,
  savedEnd: number,
  mathMlClassName: string = DEFAULT_MATH_ML_CLASS_NAME,
): number => {
  const expected = normalizeSelectionText(expectedSelectedText);
  const candidateEnds = new Set<number>([savedEnd, start + expected.length]);

  for (let offset = 0; offset <= NULL_CHAR_END_OFFSET_PROBE_LIMIT; offset++) {
    candidateEnds.add(savedEnd + offset);
    candidateEnds.add(start + expected.length + offset);
  }

  const sortedEnds = Array.from(candidateEnds)
    .filter((end) => end > start)
    .sort((a, b) => a - b);

  for (const end of sortedEnds) {
    const probeText = getTextFromCharacterOffsets(rangyApi, container, start, end, mathMlClassName);
    if (normalizeSelectionText(probeText) === expected) {
      return end;
    }
  }

  return savedEnd;
};

const applyCharacterRangeSelection = (
  selection: RangySelection,
  rangyApi: RangyRangeFactory,
  container: Node,
  start: number,
  end: number,
  backward: boolean,
  mathMlClassName: string = DEFAULT_MATH_ML_CLASS_NAME,
): string => {
  const range = createRangeFromCharacterOffsets(rangyApi, container, start, end, mathMlClassName);
  if (backward) {
    selection.setSingleRange(range, "backward");
  } else {
    selection.setSingleRange(range);
  }
  return normalizeSelectionText(selection.text());
};

const getSelectedMathSvgNodesInRange = (range: Range, annotationId?: string): SVGSVGElement[] => {
  const ancestor =
    range.commonAncestorContainer.nodeType === Node.TEXT_NODE
      ? range.commonAncestorContainer.parentElement
      : (range.commonAncestorContainer as Element);

  if (!ancestor) {
    return [];
  }

  const selectedSvgNodes: SVGSVGElement[] = [];
  ancestor.querySelectorAll('svg[role="img"]').forEach((node) => {
    if (!(node instanceof SVGSVGElement)) {
      return;
    }
    // Skip math already wrapped by this annotation. Math inside a different
    // annotation (a note created over a flash card, or the reverse) still needs
    // its own wrapper so the highlight can nest.
    if (annotationId && node.closest(`[data-annotation-id="${annotationId}"]`)) {
      return;
    }
    if (!range.intersectsNode(node)) {
      return;
    }
    selectedSvgNodes.push(node);
  });

  return selectedSvgNodes;
};

export const selectionContainsMathSvg = (range: Range): boolean => {
  return getSelectedMathSvgNodesInRange(range).length > 0;
};

export const getConsolidatedSelectedText = (selection: { text: () => string }): string => {
  const rangyText = selection.text()?.trim() ?? "";
  const nativeText =
    typeof window !== "undefined" ? window.getSelection()?.toString()?.trim() ?? "" : "";
  return nativeText.length > rangyText.length ? nativeText : rangyText;
};

export const createAnnotationBookmarkFromRange = (
  range: Range,
  commonAncestorNode: Node,
  commonAncestorCssSelector: string,
  backward: boolean,
  mathMlClassName: string = DEFAULT_MATH_ML_CLASS_NAME,
): AnnotationBookmarkOffsets => {
  const characterRange = rangeToCharacterOffsets(range, commonAncestorNode, mathMlClassName);
  return {
    start: characterRange.start,
    end: characterRange.end,
    backward,
    commonAncestorCssSelector,
  };
};

export const setSelectionByCharacterOffsets = (
  selection: RangySelection,
  rangyApi: RangyRangeFactory,
  container: Node,
  start: number,
  end: number,
  backward: boolean,
  mathMlClassName: string = DEFAULT_MATH_ML_CLASS_NAME,
): void => {
  const range = createRangeFromCharacterOffsets(rangyApi, container, start, end, mathMlClassName);
  if (backward) {
    selection.setSingleRange(range, "backward");
  } else {
    selection.setSingleRange(range);
  }
};

export const restoreAnnotationSelection = (
  selection: RangySelection,
  rangyApi: RangyRangeFactory,
  bookmark: Pick<AnnotationBookmarkOffsets, "start" | "end" | "backward">,
  commonAncestorNode: Node,
  expectedSelectedText: string,
  mathMlClassName: string = DEFAULT_MATH_ML_CLASS_NAME,
): boolean => {
  const expected = normalizeSelectionText(expectedSelectedText);
  const resolvedEnd = resolveCharacterRangeEndForText(
    rangyApi,
    commonAncestorNode,
    bookmark.start,
    expectedSelectedText,
    bookmark.end,
    mathMlClassName,
  );

  let current = applyCharacterRangeSelection(
    selection,
    rangyApi,
    commonAncestorNode,
    bookmark.start,
    resolvedEnd,
    bookmark.backward,
    mathMlClassName,
  );
  if (current === expected) {
    return true;
  }

  const legacyBookmark: LegacyBookmark = {
    backward: bookmark.backward,
    rangeBookmarks: [
      {
        start: bookmark.start,
        end: bookmark.end,
        containerNode: commonAncestorNode,
      },
    ],
  };
  selection.moveToBookmark(legacyBookmark);

  const rawCurrentSelection = ignoringDynamicContent(selection.text()?.trim() ?? "");
  const rawExpectedSelection = ignoringDynamicContent(expectedSelectedText.trim());
  const nullCharacterCountInCurrentSelection = rawCurrentSelection.split("\0").length - 1;
  const nullCharacterCountInResponseSelection = rawExpectedSelection.split("\0").length - 1;

  if (nullCharacterCountInCurrentSelection !== nullCharacterCountInResponseSelection) {
    legacyBookmark.rangeBookmarks[0].end += nullCharacterCountInCurrentSelection;
    selection.moveToBookmark(legacyBookmark);
  }

  current = normalizeSelectionText(selection.text());
  if (current === expected) {
    return true;
  }

  const legacyResolvedEnd = resolveCharacterRangeEndForText(
    rangyApi,
    commonAncestorNode,
    bookmark.start,
    expectedSelectedText,
    legacyBookmark.rangeBookmarks[0].end,
    mathMlClassName,
  );

  current = applyCharacterRangeSelection(
    selection,
    rangyApi,
    commonAncestorNode,
    bookmark.start,
    legacyResolvedEnd,
    bookmark.backward,
    mathMlClassName,
  );
  return current === expected;
};

const applyHighlightForBookmarkSelection = (
  rangyApi: RangyRangeFactory,
  container: Node,
  applier: HighlightApplier,
  bookmarkStart: number,
  bookmarkEnd: number,
  expectedSelectedText: string,
  mathMlClassName: string,
): void => {
  const resolvedEnd = resolveCharacterRangeEndForText(
    rangyApi,
    container,
    bookmarkStart,
    expectedSelectedText,
    bookmarkEnd,
    mathMlClassName,
  );
  const range = createRangeFromCharacterOffsets(
    rangyApi,
    container,
    bookmarkStart,
    resolvedEnd,
    mathMlClassName,
  );
  applyHighlightToHtmlTextInRange(rangyApi, range, applier, mathMlClassName);
};

const applyTrailingTextAfterLastMathSvg = (
  rangyApi: RangyRangeFactory,
  container: Element,
  applier: HighlightApplier,
  annotationId: string,
  expectedSelectedText: string,
  mathMlClassName: string,
): void => {
  if (!annotationId) {
    return;
  }

  const mathWrappers = container.querySelectorAll(
    `.${mathMlClassName}[data-annotation-id="${annotationId}"]`,
  );
  const mathWrapper = mathWrappers[mathWrappers.length - 1];
  if (!mathWrapper) {
    return;
  }

  const expected = normalizeSelectionText(expectedSelectedText);
  let collected = "";
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
  let node: Node | null;

  while ((node = walker.nextNode())) {
    const textNode = node as Text;
    if (isTextNodeInsideSvg(textNode)) {
      continue;
    }
    if (isChildOfMath(textNode)) {
      continue;
    }
    if (!(textNode.compareDocumentPosition(mathWrapper) & Node.DOCUMENT_POSITION_PRECEDING)) {
      continue;
    }
    // Skip text already wrapped by this annotation, but still wrap text nested
    // under a different annotation so overlapping marks can nest like Rangy.
    if (textNode.parentElement?.closest(`[data-annotation-id="${annotationId}"]`)) {
      continue;
    }

    const trial = normalizeSelectionText(collected + textNode.data);
    if (trial.length > 0 && !expected.endsWith(trial) && !expected.includes(trial)) {
      break;
    }

    const nodeRange = rangyApi.createRange();
    nodeRange.selectNodeContents(textNode);
    applier.applyToRange(nodeRange);
    collected += textNode.data;

    if (expected.endsWith(trial)) {
      break;
    }
  }
};

export const applyAnnotationTextAndMathHighlights = ({
  rangyApi,
  container,
  applier,
  bookmarkStart,
  bookmarkEnd,
  selectedText,
  annotationId,
  mathMlClassName,
  selectionRange,
  wrapMathSvgs,
}: {
  rangyApi: RangyRangeFactory;
  container: Node;
  applier: HighlightApplier;
  bookmarkStart: number;
  bookmarkEnd: number;
  selectedText: string;
  annotationId: string;
  mathMlClassName: string;
  selectionRange: Range;
  wrapMathSvgs: (svgNodes: SVGSVGElement[]) => void;
}): void => {
  const mathSvgNodes = getSelectedMathSvgNodesInRange(selectionRange, annotationId).filter(
    (node) => node.isConnected,
  );

  applyHighlightForBookmarkSelection(
    rangyApi,
    container,
    applier,
    bookmarkStart,
    bookmarkEnd,
    selectedText,
    mathMlClassName,
  );

  if (!mathSvgNodes.length) {
    return;
  }

  wrapMathSvgs(mathSvgNodes);
  applyTrailingTextAfterLastMathSvg(
    rangyApi,
    container as Element,
    applier,
    annotationId,
    selectedText,
    mathMlClassName,
  );
};
